import React from 'react'
import { Analytics } from "@vercel/analytics/react"
import { Helmet } from 'react-helmet'
import { Header, Footer } from "./LandingPage"
import {
  Calendar, Clock, MapPin,
  Waves, Thermometer, ArrowDown, Eye, Wind, Droplet, Users, Star,
  ChevronLeft, ChevronRight, ImageIcon, MessageSquare, X,
} from "lucide-react"
import SEO from './common/SEO'
import { getDestinations } from '../lib/destinations-data'
import type { Destination, SiteCard as SiteCardT } from '../lib/destinations'
import { BASE_URL } from '../lib/routes'
import { cn } from '../lib/utils'
import { useParams, Link } from 'react-router-dom'

// Same icon vocabulary the app's DiveLogDetailView uses (SF Symbols mapped to
// their closest lucide equivalents): depth = arrow.down, dive time = clock,
// water temp = thermometer, visibility = eye, current = wind, water = drop.
const ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  waves: Waves,
  users: Users,
  mapPin: MapPin,
  calendar: Calendar,
  thermometer: Thermometer,
  arrowDown: ArrowDown,
  clock: Clock,
  eye: Eye,
  wind: Wind,
  droplet: Droplet,
}

// Matches the app's tints: depth cyan, dive time orange, everything else the
// landing's brand blue.
const TINTS: Record<string, string> = {
  cyan: 'text-cyan-500',
  orange: 'text-orange-500',
}
const DEFAULT_TINT = 'text-[#1F80FF]'

// Top summary strip, echoing the app's 3-up DiveDetailSummaryCard row.
const StatStrip: React.FC<{ stats: NonNullable<Destination['stats']> }> = ({ stats }) => {
  if (!stats || stats.length === 0) return null;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
      {stats.map((s, i) => {
        const Icon = ICONS[s.icon] ?? Waves;
        return (
          <div key={i} className="rounded-2xl border border-border bg-white px-4 py-3 shadow-sm">
            <div className="flex items-center gap-1.5 text-[#1F80FF]">
              <Icon className="w-4 h-4" />
              <span className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">{s.label}</span>
            </div>
            <div className="mt-1.5 text-xl font-black tracking-tight tabular-nums">{s.value}</div>
          </div>
        );
      })}
    </div>
  );
};

// Environmental conditions, echoing the app's 2x2 DiveDetailConditionCard grid.
const ConditionsGrid: React.FC<{ conditions: NonNullable<Destination['conditions']> }> = ({ conditions }) => {
  if (!conditions || conditions.length === 0) return null;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-10">
      {conditions.map((c, i) => {
        const Icon = ICONS[c.icon] ?? Droplet;
        const tint = (c.tint && TINTS[c.tint]) || DEFAULT_TINT;
        return (
          <div key={i} className="rounded-2xl border border-border bg-white px-4 py-3 shadow-sm">
            <div className="flex items-center gap-1.5">
              <Icon className={cn("w-4 h-4", tint)} />
              <span className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">{c.label}</span>
            </div>
            <div className="mt-1.5 font-black tracking-tight tabular-nums">
              {c.value}
              {c.unit && <span className="ml-1 text-sm font-semibold text-muted-foreground">{c.unit}</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const Stars: React.FC<{ rating: number; className?: string }> = ({ rating, className }) => (
  <span className={cn("inline-flex items-center gap-0.5", className)} aria-label={`${rating.toFixed(1)} out of 5`}>
    {[0, 1, 2, 3, 4].map(i => (
      <Star key={i} className={cn("w-3.5 h-3.5", i < Math.round(rating) ? "fill-amber-400 text-amber-400" : "text-gray-300")} />
    ))}
  </span>
);

// Full-screen detail for a single site: the complete photo gallery plus every
// written review. Opened by tapping a card.
const SiteModal: React.FC<{ site: SiteCardT; place: string; onClose: () => void }> = ({ site, place, onClose }) => {
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`${site.name} details`}
    >
      <div
        className="bg-white w-full sm:max-w-2xl max-h-[90vh] sm:max-h-[85vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white/95 backdrop-blur border-b border-border px-5 py-4 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-xl font-bold leading-tight">{site.name}</h3>
            <p className="text-sm text-muted-foreground">{place}</p>
            {site.rating != null && (
              <div className="mt-1 flex items-center gap-2">
                <Stars rating={site.rating} />
                <span className="text-sm font-semibold tabular-nums">{site.rating.toFixed(1)}</span>
                {site.reviews ? <span className="text-sm text-muted-foreground">· {site.reviews} rating{site.reviews > 1 ? 's' : ''}</span> : null}
              </div>
            )}
          </div>
          <button type="button" aria-label="Close" onClick={onClose} className="shrink-0 w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-6">
          {site.blurb && <p className="text-foreground/80 leading-relaxed">{site.blurb}</p>}

          {site.photos && site.photos.length > 0 && (
            <div>
              <h4 className="text-sm font-bold uppercase tracking-wide text-muted-foreground mb-2">Photos from the community</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {site.photos.map((src, i) => (
                  <div key={i} className="aspect-square overflow-hidden rounded-xl bg-gray-100">
                    <img src={src} alt={`${site.name} dive photo`} loading="lazy" className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {site.reviewList && site.reviewList.length > 0 && (
            <div>
              <h4 className="text-sm font-bold uppercase tracking-wide text-muted-foreground mb-3">Reviews from divers</h4>
              <div className="space-y-3">
                {site.reviewList.map((r, i) => {
                  const name = r.displayName || (r.username ? `@${r.username}` : 'A Currents diver');
                  const initial = (r.displayName || r.username || 'C').charAt(0).toUpperCase();
                  return (
                  <div key={i} className="rounded-2xl border border-border p-4">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2 min-w-0">
                        {r.avatarUrl ? (
                          <img src={r.avatarUrl} alt="" className="w-7 h-7 rounded-full object-cover shrink-0" />
                        ) : (
                          <span className="w-7 h-7 rounded-full bg-[#1F80FF]/10 text-[#1F80FF] text-xs font-bold flex items-center justify-center shrink-0">{initial}</span>
                        )}
                        <span className="text-sm font-medium truncate">{name}</span>
                      </div>
                      {r.date && <span className="text-xs text-muted-foreground shrink-0">{new Date(r.date).toLocaleDateString(undefined, { year: 'numeric', month: 'short' })}</span>}
                    </div>
                    {r.rating != null && <div className="mb-1.5"><Stars rating={r.rating} /></div>}
                    <p className="text-sm text-foreground/90 leading-relaxed">{r.comment}</p>
                  </div>
                  );
                })}
              </div>
            </div>
          )}

          {!(site.reviewList && site.reviewList.length) && (
            <p className="text-sm text-muted-foreground">No written reviews yet. Ratings shown are from divers who logged this site.</p>
          )}
        </div>
      </div>
    </div>
  );
};

// One card per dive site, in a horizontal scroll-snap carousel. Each card
// carries the site's own community photos, its star rating (shown even where
// there is no written review), and a short description. Tapping a card opens
// the full photo gallery and written reviews.
const SiteCards: React.FC<{ sites: NonNullable<Destination['siteCards']>; place: string }> = ({ sites, place }) => {
  const scroller = React.useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = React.useState(false);
  const [canRight, setCanRight] = React.useState(false);
  const [open, setOpen] = React.useState<number | null>(null);

  const update = React.useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 8);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  }, []);

  React.useEffect(() => {
    update();
    const el = scroller.current;
    if (!el) return;
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [update]);

  const scroll = (dir: 1 | -1) => {
    const el = scroller.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.8), behavior: 'smooth' });
  };

  if (!sites || sites.length === 0) return null;
  return (
    <div className="mb-10">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xl font-bold">Dive sites</h2>
        <div className="hidden sm:flex gap-2">
          <button
            type="button"
            aria-label="Scroll left"
            onClick={() => scroll(-1)}
            disabled={!canLeft}
            className="w-9 h-9 rounded-full border border-border bg-white flex items-center justify-center text-foreground/70 hover:text-foreground disabled:opacity-30 disabled:cursor-default transition"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            type="button"
            aria-label="Scroll right"
            onClick={() => scroll(1)}
            disabled={!canRight}
            className="w-9 h-9 rounded-full border border-border bg-white flex items-center justify-center text-foreground/70 hover:text-foreground disabled:opacity-30 disabled:cursor-default transition"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
      <div className="-mx-4 sm:mx-0">
        <div ref={scroller} className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-px-4 px-4 sm:px-0 pb-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
          {sites.map((s, i) => {
            const photoCount = s.photos?.length ?? 0;
            const reviewCount = s.reviewList?.length ?? 0;
            return (
            <button
              key={i}
              type="button"
              onClick={() => setOpen(i)}
              className="group text-left snap-start shrink-0 w-[82%] sm:w-[54%] lg:w-[40%] rounded-2xl border border-border bg-white overflow-hidden flex flex-col hover:shadow-lg hover:border-[#1F80FF]/30 transition cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1F80FF]"
            >
              {s.photos && s.photos.length > 0 ? (
                <div className="relative flex gap-1 h-44 bg-gray-100">
                  <div className="flex-1 overflow-hidden">
                    <img src={s.photos[0]} alt={`${s.name} dive photo`} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300" />
                  </div>
                  {s.photos[1] && (
                    <div className="hidden sm:block w-1/3 overflow-hidden">
                      <img src={s.photos[1]} alt={`${s.name} dive photo`} loading="lazy" className="w-full h-full object-cover" />
                    </div>
                  )}
                  {photoCount > 2 && (
                    <span className="absolute bottom-2 right-2 rounded-full bg-black/60 text-white text-xs font-semibold px-2 py-1">+{photoCount - 2} photos</span>
                  )}
                </div>
              ) : (
                <div className="h-16 bg-gradient-to-br from-sky-50 to-blue-100" />
              )}
              <div className="p-4 flex flex-col gap-1.5 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-bold leading-snug">{s.name}</span>
                  {s.rating != null && (
                    <span className="flex items-center gap-1 text-sm shrink-0">
                      <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                      <span className="font-semibold tabular-nums">{s.rating.toFixed(1)}</span>
                      {s.reviews ? <span className="text-muted-foreground">({s.reviews})</span> : null}
                    </span>
                  )}
                </div>
                {s.blurb && <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2">{s.blurb}</p>}
                <div className="mt-auto pt-2 flex items-center gap-3 text-xs font-medium text-[#1F80FF]">
                  {photoCount > 0 && <span className="flex items-center gap-1"><ImageIcon className="w-3.5 h-3.5" />{photoCount}</span>}
                  {reviewCount > 0 && <span className="flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" />{reviewCount} review{reviewCount > 1 ? 's' : ''}</span>}
                  <span className="ml-auto flex items-center gap-0.5 group-hover:gap-1.5 transition-all">View<ChevronRight className="w-3.5 h-3.5" /></span>
                </div>
              </div>
            </button>
            );
          })}
        </div>
      </div>
      {open != null && sites[open] && (
        <SiteModal site={sites[open]} place={place} onClose={() => setOpen(null)} />
      )}
    </div>
  );
};

const DestinationPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = React.useState<Destination | null>(null);
  const [related, setRelated] = React.useState<Destination[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    async function load() {
      if (!slug) return;
      try {
        const all = await getDestinations();
        const found = all.find(p => p.slug === slug);
        setPost(found || null);
        if (found) {
          setRelated(all.filter(p => p.slug !== found.slug).slice(0, 3));
        }
      } catch (error) {
        console.error('Error loading destination:', error);
      } finally {
        setIsLoading(false);
      }
    }
    load();
  }, [slug]);

  if (isLoading) return <div>Loading...</div>;
  if (!post) return <div>Destination not found</div>;

  return (
    <div className="flex min-h-screen flex-col relative">
      <SEO
        path={`/destinations/${post.slug}`}
        title={post.meta.title}
        description={post.meta.description}
        image={post.coverImage || undefined}
        type="article"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title,
            description: post.meta.description,
            ...(post.coverImage
              ? { image: post.coverImage.startsWith('http') ? post.coverImage : `${BASE_URL}${post.coverImage}` }
              : {}),
            datePublished: post.date,
            author: {
              "@type": "Organization",
              name: "Currents"
            },
            publisher: {
              "@type": "Organization",
              name: "Currents",
              logo: { "@type": "ImageObject", url: `${BASE_URL}/currents-logo.png` }
            },
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": `${BASE_URL}/destinations/${post.slug}`
            }
          })}
        </script>
      </Helmet>
      <Header />
      <main className="flex-1 pt-24">
        <div className="container max-w-4xl">
          {/* Title block */}
          <div className="mb-5">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium text-[#1F80FF]">
              <MapPin className="w-4 h-4" />
              {post.region || post.category}
            </div>
            <h1 className="text-4xl font-black tracking-tighter sm:text-5xl leading-[0.95]">{post.title}</h1>
            <p className="mt-3 text-lg text-muted-foreground max-w-2xl">{post.excerpt}</p>
          </div>

          {/* Facts panel: the differentiator, up top like an info page */}
          {post.stats && <StatStrip stats={post.stats} />}
          {post.conditions && <ConditionsGrid conditions={post.conditions} />}

          {/* Dive sites: photos + rating + description per site, one carousel */}
          {post.siteCards && <SiteCards sites={post.siteCards} place={post.region || post.title} />}
          {post.siteCards && post.siteCards.some(s => s.photos?.length) && (
            <p className="-mt-6 mb-10 text-xs text-muted-foreground">Photos from dives logged here by the Currents community.</p>
          )}

          {/* Supporting write-up, demoted below the facts */}
          <div
            className="prose max-w-none mt-4
              prose-headings:font-bold prose-headings:tracking-tight
              prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-3
              prose-p:leading-relaxed prose-p:mb-5 prose-p:text-foreground/80
              prose-strong:font-semibold
              prose-a:text-[#1F80FF] prose-a:no-underline hover:prose-a:underline"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          <p className="mt-8 text-xs text-muted-foreground">
            Compiled from dives the Currents community logged and shared here. Numbers reflect logged public dives and update as more are added.
          </p>

          {/* Download CTA, styled like the Dive Passport MakeYourOwnCard */}
          <a
            href="https://apps.apple.com/app/id6504115639"
            className="group block mt-6 rounded-3xl overflow-hidden border border-border"
            style={{ background: 'linear-gradient(180deg, #DCE8F8, #F2F6FC)' }}
          >
            <div className="px-6 py-8 text-center">
              <img src="/currents-logo.png" alt="" className="w-11 h-11 rounded-xl mx-auto mb-3" />
              <div className="text-lg font-extrabold text-[#14213D] leading-snug">
                Dived {post.region?.split(',')[0] || 'here'}? Add it to Currents.
              </div>
              <p className="text-sm text-[#14213D]/60 mt-1.5 max-w-md mx-auto leading-relaxed">
                Log your dives, rate the sites, and leave reviews. Your logs help build pages like this one.
              </p>
              <span className="inline-block mt-4 rounded-full bg-[#14213D] text-white font-bold text-sm px-7 py-3 group-hover:-translate-y-0.5 transition-transform">
                Get Currents — free
              </span>
            </div>
          </a>

          {related.length > 0 && (
            <div className="mt-12 border-t border-border pt-10">
              <h2 className="text-2xl font-bold mb-6">More destinations</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {related.map(r => (
                  <Link key={r.slug} to={`/destinations/${r.slug}`} className="group rounded-2xl border border-border bg-white p-5">
                    <h3 className="font-semibold leading-snug group-hover:text-[#1F80FF] transition-colors">{r.title}</h3>
                    {r.region && <p className="text-sm text-muted-foreground mt-1">{r.region}</p>}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
      <Analytics />
    </div>
  )
}

export default DestinationPostPage
